/**
 * @file        src/lib/ticketNumber.js
 * @description Client-side mirror of the server's ticket-number rule
 *              (admin_backend/src/utils/ticketNumber.js). The server is the
 *              boundary that enforces it — this exists so the Update Ticket form
 *              can flag a bad entry inline instead of round-tripping a 400.
 *              Valid: 13 digits (IATA — 3-digit airline code + 10-digit serial,
 *              separators optional) or a 6-20 character alphanumeric LCC
 *              reference. KEEP IN STEP WITH THE SERVER COPY.
 * @author      Kinetika Labs
 * @created     2026-09-09
 * @changelog
 *   - 2026-09-09 Claude — Initial (ticket number validation).
 */
const IATA_DIGITS = 13;
const LCC_MIN = 6;
const LCC_MAX = 20;

export const TICKET_NUMBER_MESSAGE =
  'Enter a valid ticket number — 13 digits (e.g. 098-1234567890) or a 6-20 character airline reference.';

/** Canonical form, or null when the value is not a ticket number. */
export function normalizeTicketNumber(value) {
  const raw = String(value ?? '').replace(/[\s.\-–—]/g, '');
  if (!raw) return null;
  if (/^\d+$/.test(raw)) {
    if (raw.length !== IATA_DIGITS) return null;
    return `${raw.slice(0, 3)}-${raw.slice(3)}`;
  }
  if (!/^[A-Za-z0-9]+$/.test(raw)) return null;
  if (raw.length < LCC_MIN || raw.length > LCC_MAX) return null;
  return raw.toUpperCase();
}

export function isValidTicketNumber(value) {
  return normalizeTicketNumber(value) !== null;
}
